
import React, { useState } from 'react';

interface VideoCardProps {
  title: string;
  highlight: string;
  description: string;
  videoId: string;
  tags: { icon: string; label: string }[];
}

/**
 * VideoCard – renders a thumbnail that switches to an embedded YouTube player
 * when the user clicks the play button.
 *
 * Robustness improvements:
 *   • Handles image load errors by falling back to the default YouTube thumbnail.
 *   • Catches unexpected errors when toggling the playing state.
 */
function VideoCard({ title, highlight, description, videoId, tags }: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  const handlePlay = () => {
    try {
      setIsPlaying(true);
    } catch (e) {
      console.error('Failed to start video playback:', e);
    }
  };

  const thumbnailUrl = thumbError
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` // fallback thumbnail
    : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <div className="flex flex-col">
      <div className="relative rounded-2xl overflow-hidden border border-gold/10 shadow-lg shadow-gold/5">
        {!isPlaying ? (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <img
              src={thumbnailUrl}
              alt={`${title} ${highlight} - Vídeo`}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setThumbError(true)}
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <button
                onClick={handlePlay}
                className="group flex flex-col items-center gap-3 cursor-pointer"
                aria-label={`Reproduzir vídeo - ${title} ${highlight}`}
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gold/30 rounded-full animate-ping"></div>
                  <div className="relative w-16 h-16 flex items-center justify-center bg-gold rounded-full group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-gold/30">
                    <i className="ri-play-fill text-2xl text-black ml-0.5"></i>
                  </div>
                </div>
                <span className="text-white font-display font-semibold text-sm group-hover:text-gold transition-colors duration-300">
                  Assistir
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&start=0`}
              title={`${title} ${highlight}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              aria-label={`Vídeo - ${title} ${highlight}`}
            ></iframe>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <h3 className="font-display text-2xl font-bold text-white leading-tight">
          {title} <span className="text-gold">{highlight}</span>
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {tags.map((tag, index) => (
          <div
            key={index}
            className="flex items-center gap-2.5 p-3 rounded-xl bg-black/40 border border-gold/5 hover:border-gold/20 transition-all duration-300"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/10 shrink-0">
              <i className={`${tag.icon} text-base text-gold`}></i>
            </div>
            <span className="text-gray-300 text-xs font-medium leading-snug">{tag.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * NossosVideos – section that lists a set of video cards.
 * Includes a subtle background pattern and proper ARIA labeling.
 */
export function NossosVideos() {
  const videos: VideoCardProps[] = [
    {
      title: 'Circuito Hip-Hop',
      highlight: 'Pelas Comunidades',
      description:
        'Veja como a cultura transforma territórios. O Circuito Hip-Hop leva formação, apresentações e oportunidades reais para comunidades periféricas.',
      videoId: 'gBmUtQvUWOg',
      tags: [
        { icon: 'ri-mic-line', label: 'Artistas Remunerados' },
        { icon: 'ri-community-line', label: 'Comunidades Atendidas' },
        { icon: 'ri-graduation-cap-line', label: 'Formação Cultural' },
        { icon: 'ri-star-line', label: 'Talentos Revelados' },
      ],
    },
    {
      title: 'Feira Afro',
      highlight: 'de Atitude',
      description:
        'Uma plataforma que conecta cultura, identidade e desenvolvimento econômico, valorizando o empreendedorismo afro-brasileiro e a economia criativa.',
      videoId: 'FBDZYhBVT2Y',
      tags: [
        { icon: 'ri-store-2-line', label: 'Empreendedores Fortalecidos' },
        { icon: 'ri-money-dollar-circle-line', label: 'Geração de Renda' },
        { icon: 'ri-hand-heart-line', label: 'Integração Cultural' },
        { icon: 'ri-map-pin-line', label: 'Expansão Territorial' },
      ],
    },
  ];

  return (
    <section
      id="nossos-videos"
      className="py-40 px-8 lg:px-20 bg-gradient-to-r from-dark via-dark-light to-dark relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="flex gap-8 justify-center items-center h-full">
          {[1, 2, 3, 4, 5].map((i) => (
            <i key={i} className="ri-film-line text-gold text-[200px]"></i>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">
            Em Ação
          </span>
          <h2 className="font-display text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
            Nossos Projetos
          </h2>
          <p className="text-xl text-gold font-medium italic">Cultura que transforma territórios.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {videos.map((video, index) => (
            <VideoCard key={index} {...video} />
          ))}
        </div>
      </div>
    </section>
  );
}
